import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { first } from "rxjs/operators";

import { LorenFieldTypes } from "../../../../../../shared/modules/loren-entity-form-card/enums/loren-field-types.enum";
import { LorenEntityFormModel } from "../../../../../../shared/modules/loren-entity-form-card/interfaces/loren-entity-form-model.interface.ts";
import { LorenFormSubmission } from "../../../../../../shared/modules/loren-entity-form-card/interfaces/loren-form-submission.interface";
import { RegexPatterns } from "../../../../../../shared/constants/regex-patterns.constants";
import { LorenAuthenticationService } from "../../../../../../shared/services/loren-authentication-service/loren-authentication.service";
import { LorenUsersEndpointsService } from "../../../../../../shared/services/loren-users-endpoints-service/loren-users-endpoints.service";
import { LorenHttpResponse } from "../../../../../../shared/interfaces/loren-http-response.interface";
import { LorenInputTypes } from "../../../../../../shared/modules/loren-entity-form/components/loren-entity-form-field/constants/loren-input-types.constants";
import { LorenHttpResponseError } from "../../../../../../shared/interfaces/loren-http-response-error.interface";
import { LorenHttpErrorTranslationService } from "../../../../../../shared/services/loren-http-error-translation-service/loren-http-error-translation.service";
import { LorenSnackBarService } from "../../../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenCardModule } from "../../../../../../shared/modules/loren-card/loren-card.module";
import { LorenEntityFormModule } from "../../../../../../shared/modules/loren-entity-form/loren-entity-form.module";
import { MatButton } from "@angular/material/button";

@Component({
  imports: [MatButton, LorenCardModule, LorenEntityFormModule, TranslocoPipe],
  selector: "loren-user-profile-password",
  standalone: true,
  styleUrls: ["./loren-user-profile-password.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./loren-user-profile-password.component.html",
})
export class LorenUserProfilePasswordComponent implements OnInit {
  private http = inject(HttpClient);
  private lorenUsersEndpointsService = inject(LorenUsersEndpointsService);
  private lorenAuthenticationService = inject(LorenAuthenticationService);
  private translateService = inject(TranslocoService);
  private lorenHttpErrorTranslationServiceService = inject(LorenHttpErrorTranslationService);
  private lorenSnackBarService = inject(LorenSnackBarService);


  private static readonly CURRENT_PASSWORD_FIELD_NAME: string =
    "current_password";
  private static readonly NEW_PASSWORD_FIELD_NAME: string = "new_password";
  private static readonly NEW_PASSWORD_CONFIRM_FIELD_NAME: string =
    "confirm_new_password";

  userChangePasswordFormModel: LorenEntityFormModel;
  isChangingPassword: boolean;
  errors: string[] = [];
  disableSubmitBtn: boolean;

  ngOnInit(): void {
    this.buildUserChangePasswordFormModel();
    this.disableSubmitBtn = true;
  }

  toggleIsChangingPassword() {
    this.isChangingPassword = !this.isChangingPassword;
    this.errors = [];
  }

  handleFormSubmit(formSubmission: LorenFormSubmission) {
    if (formSubmission.valid) {
      const userInfo = this.lorenAuthenticationService.getUserInfo();
      if (
        formSubmission.value[
          LorenUserProfilePasswordComponent.NEW_PASSWORD_FIELD_NAME
        ] ===
        formSubmission.value[
          LorenUserProfilePasswordComponent.NEW_PASSWORD_CONFIRM_FIELD_NAME
        ]
      ) {
        this.disableSubmitBtn = true;
        this.http
          .post(this.lorenUsersEndpointsService.password(userInfo.id), {
            currentPassword:
              formSubmission.value[
                LorenUserProfilePasswordComponent.CURRENT_PASSWORD_FIELD_NAME
              ],
            newPassword:
              formSubmission.value[
                LorenUserProfilePasswordComponent
                  .NEW_PASSWORD_CONFIRM_FIELD_NAME
              ],
          })
          .subscribe({
            next: (res) => {
              const response = res as LorenHttpResponse;
              if (response.success) {
                this.toggleIsChangingPassword();
                this.disableSubmitBtn = true;
                this.lorenSnackBarService.showSuccessMessage();
                this.buildUserChangePasswordFormModel();
              }
            },
            error: (error) => {
              const responseErrors = error.error
                .errors as LorenHttpResponseError[];
              this.errors =
                this.lorenHttpErrorTranslationServiceService.getTranslatedErrorsFromResponse(
                  responseErrors
                );
              this.disableSubmitBtn = false;
            },
          });
      } else {
        this.translateService
          .selectTranslate("errors.new_password_mismatch")
          .pipe(first())
          .subscribe((translation) => {
            this.errors = [translation];
          });
      }
    }
  }

  private buildUserChangePasswordFormModel() {
    this.userChangePasswordFormModel = {
      formName: "user.change_password",
      formFields: [
        {
          name: LorenUserProfilePasswordComponent.CURRENT_PASSWORD_FIELD_NAME,
          label: "user.current_password",
          formControlType: LorenFieldTypes.INPUT,
          validator: {
            required: true,
          },
          inputType: LorenInputTypes.PASSWORD,
          options: [],
        },
        {
          name: LorenUserProfilePasswordComponent.NEW_PASSWORD_FIELD_NAME,
          label: "user.new_password",
          formControlType: LorenFieldTypes.INPUT,
          validator: {
            required: true,
            minLength: 6,
            maxLength: 30,
            pattern: RegexPatterns.PASSWORD,
          },
          hint: "user.password_requirements",
          translateHint: true,
          inputType: LorenInputTypes.PASSWORD,
          options: [],
        },
        {
          name: LorenUserProfilePasswordComponent.NEW_PASSWORD_CONFIRM_FIELD_NAME,
          label: "user.confirm_new_password",
          formControlType: LorenFieldTypes.INPUT,
          validator: {
            required: true,
          },
          inputType: LorenInputTypes.PASSWORD,
          options: [],
        },
      ],
    };
  }
}
